import { useState, useEffect } from 'react';

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-32 pb-16 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a2942] via-[#2a4b7c] to-[#1e3a5f]"></div>
      
      {/* Animated grid pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
          animation: 'gridMove 20s linear infinite'
        }}></div>
      </div>

      {/* Floating orbs */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-[#06b6d4]/20 rounded-full blur-3xl animate-[float_8s_ease-in-out_infinite]"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#06b6d4]/10 rounded-full blur-3xl animate-[float_10s_ease-in-out_infinite]" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-1/2 left-1/3 w-56 h-56 bg-[#0891b2]/10 rounded-full blur-3xl animate-[float_12s_ease-in-out_infinite]" style={{ animationDelay: '4s' }}></div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white rounded-full opacity-20"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-md transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
            }`}>
              <div className="relative">
                <div className="w-2 h-2 rounded-full bg-[#06b6d4]"></div>
                <div className="absolute inset-0 w-2 h-2 rounded-full bg-[#06b6d4] animate-ping"></div>
              </div>
              <span className="text-white/90 text-sm font-medium tracking-wide">Phát Triển Phần Mềm Theo Yêu Cầu</span>
            </div>

            {/* Main Heading */}
            <div className={`space-y-4 transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
              <h1 className="font-['Inter',sans-serif] text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.2]">
                <span className="bg-gradient-to-r from-white via-[#06b6d4] to-white bg-clip-text text-transparent">
                  Giải pháp phần mềm được thiết kế từ chính nghiệp vụ của doanh nghiệp
                </span>
              </h1>
            </div>

            {/* Description */}
            <p className={`text-sm md:text-base text-white/70 leading-relaxed max-w-xl transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
              Stackway cung cấp dịch vụ <strong className="text-white/90 font-semibold">phát triển phần mềm theo yêu cầu</strong>, tập trung giải quyết bài toán nghiệp vụ cốt lõi, giúp doanh nghiệp xây dựng hệ thống phù hợp thực tế vận hành, ổn định dài hạn và sẵn sàng mở rộng.
            </p>

            {/* CTA Buttons */}
            <div className={`flex flex-col sm:flex-row items-start gap-4 pt-4 transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
              <button 
                onClick={scrollToContact}
                className="group relative bg-[#06b6d4] text-white px-7 py-3.5 rounded-full text-base font-semibold hover:bg-[#0891b2] transition-all duration-300 flex items-center gap-2 cursor-pointer whitespace-nowrap overflow-hidden shadow-xl hover:shadow-2xl hover:shadow-[#06b6d4]/50 hover:scale-105"
              >
                <span className="relative z-10">Trao đổi bài toán của bạn</span>
                <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform relative z-10"></i>
                <div className="absolute inset-0 bg-gradient-to-r from-[#0891b2] to-[#06b6d4] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
              <button 
                onClick={() => {
                  const section = document.getElementById('services') || document.querySelector('[data-section]');
                  if (section) {
                    section.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="group bg-transparent border-2 border-white/30 text-white px-7 py-3.5 rounded-full text-base font-semibold hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                Tìm hiểu thêm
              </button>
            </div>
          </div>

          {/* Right: Visual */}
          <div className={`relative hidden lg:block transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}>
            <div className="relative h-full flex items-center">
              {/* Main card with glass effect */}
              <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-white/5 backdrop-blur-xl p-10 lg:p-12 shadow-2xl hover:scale-[1.02] transition-all duration-500 w-full min-h-[500px] flex flex-col justify-center group">
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-white/5 to-transparent group-hover:from-white/15 group-hover:via-white/8 transition-all duration-500"></div>
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#06b6d4]/10 via-transparent to-[#00c897]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                {/* Animated background grid */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)`,
                    backgroundSize: '20px 20px',
                  }}></div>
                </div>
                
                {/* Code visualization */}
                <div className="relative z-10 space-y-6">
                  <div className="flex items-center gap-2.5 mb-6">
                    <div className="w-4 h-4 rounded-full bg-red-500 shadow-lg shadow-red-500/50 animate-pulse"></div>
                    <div className="w-4 h-4 rounded-full bg-yellow-500 shadow-lg shadow-yellow-500/50 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-4 h-4 rounded-full bg-green-500 shadow-lg shadow-green-500/50 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                  
                  <div className="space-y-4 font-mono text-base lg:text-lg leading-relaxed">
                    <div className={`flex gap-4 items-center transition-all duration-1000 delay-300 ${
                      isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                    }`}>
                      <span className="text-[#06b6d4] font-semibold italic">const</span>
                      <span className="text-white font-medium">solution</span>
                      <span className="text-white/80">=</span>
                      <span className="text-[#00c897] font-bold animate-pulse">{`{`}</span>
                    </div>
                    <div className="pl-5 space-y-3">
                      <div className={`flex gap-4 items-center transition-all duration-1000 delay-500 ${
                        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                      }`}>
                        <span className="text-white/70 font-light italic">type:</span>
                        <span className="text-[#00c897] font-medium italic relative group/item">
                          <span className="relative z-10">'custom'</span>
                          <span className="absolute inset-0 bg-[#00c897]/20 blur-sm opacity-0 group-hover/item:opacity-100 transition-opacity duration-300"></span>
                        </span>
                      </div>
                      <div className={`flex gap-4 items-center transition-all duration-1000 delay-700 ${
                        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                      }`}>
                        <span className="text-white/70 font-light italic">business:</span>
                        <span className="text-[#00c897] font-medium italic relative group/item">
                          <span className="relative z-10">'focused'</span>
                          <span className="absolute inset-0 bg-[#00c897]/20 blur-sm opacity-0 group-hover/item:opacity-100 transition-opacity duration-300"></span>
                        </span>
                      </div>
                      <div className={`flex gap-4 items-center transition-all duration-1000 delay-900 ${
                        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                      }`}>
                        <span className="text-white/70 font-light italic">scalable:</span>
                        <span className="text-[#06b6d4] font-semibold italic relative group/item">
                          <span className="relative z-10">true</span>
                          <span className="absolute inset-0 bg-[#06b6d4]/20 blur-sm opacity-0 group-hover/item:opacity-100 transition-opacity duration-300"></span>
                        </span>
                      </div>
                    </div>
                    <div className={`text-[#00c897] font-bold transition-all duration-1000 delay-1100 ${
                      isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                    }`}>
                      {`}`}
                      <span className="inline-block w-0.5 h-5 bg-[#06b6d4] ml-2 animate-blink"></span>
                    </div>
                  </div>
                  
                  {/* Cursor blinking effect */}
                  <div className={`flex items-center gap-2 mt-4 transition-all duration-1000 delay-1300 ${
                    isVisible ? 'opacity-100' : 'opacity-0'
                  }`}>
                    <span className="text-white/40 text-sm italic">// Phần mềm phù hợp với nghiệp vụ</span>
                    <span className="w-2 h-5 bg-[#06b6d4] animate-pulse"></span>
                  </div>
                </div>
                
                {/* Floating badges */}
                <div className="absolute top-6 right-6 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-4 py-3 animate-[float_3s_ease-in-out_infinite] z-20">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#06b6d4] to-[#0891b2] flex items-center justify-center">
                      <i className="ri-code-s-slash-line text-white text-lg"></i>
                    </div>
                    <div>
                      <div className="text-white text-sm font-semibold">Custom</div>
                      <div className="text-white/60 text-xs">Software</div>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-6 left-6 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-4 py-3 animate-[float_3s_ease-in-out_infinite] z-20" style={{ animationDelay: '1s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0891b2] to-[#06b6d4] flex items-center justify-center">
                      <i className="ri-rocket-line text-white text-lg"></i>
                    </div>
                    <div>
                      <div className="text-white text-sm font-semibold">Scalable</div>
                      <div className="text-[#06b6d4] text-xs font-bold">100%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#06b6d4]/20 rounded-full blur-3xl animate-[pulse_4s_ease-in-out_infinite]"></div>
              <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-[#0891b2]/15 rounded-full blur-3xl animate-[pulse_4s_ease-in-out_infinite]" style={{ animationDelay: '2s' }}></div>
              
              {/* Floating particles */}
              <div className="absolute top-1/4 -left-3 w-2.5 h-2.5 bg-[#06b6d4] rounded-full animate-[float_5s_ease-in-out_infinite]"></div>
              <div className="absolute top-1/2 -right-3 w-2 h-2 bg-[#0891b2] rounded-full animate-[float_6s_ease-in-out_infinite]" style={{ animationDelay: '1s' }}></div>
              <div className="absolute bottom-1/4 -left-4 w-2 h-2 bg-[#06b6d4] rounded-full animate-[float_7s_ease-in-out_infinite]" style={{ animationDelay: '2s' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-3xl text-white/50"></i>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes gridMove {
          0% { transform: translateY(0); }
          100% { transform: translateY(50px); }
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        .animate-blink {
          animation: blink 1s infinite;
        }
      `}</style>
    </section>
  );
}
